/**
 * What changing a set's TYPE does to the set — the pure patch, and the vocabulary of types the web
 * authors.
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/setTypeChange.ts` and the AMRAP half
 * of `progressionAmrap.ts`. The structural half — binding or creating a top, and keeping the block
 * whole afterwards — is one level up, in `draftOperations`, because it adds and removes rows rather
 * than patching one.
 *
 * ── The five types the web authors, and the sixth it will not ────────────────────────────────
 * Execution ADR §1: `Working`, `Warmup`, `Top`, `Backoff`, `AMRAP`. **`Dropset` is a conscious
 * deferral** — the web does not author one, but an inbound dropset from the phone renders raw and
 * round-trips unchanged rather than being dropped or rewritten. `AUTHORABLE_SET_TYPES` is what the
 * cycle and the menu offer; `setTypePatch` will still transition a dropset OUT if asked, because
 * the trainer choosing a real type is a decision they made.
 *
 * ── The transitions ──────────────────────────────────────────────────────────────────────────
 * · **Backoff** → seeds the percent from `backoffPick` (its own, else the last descent's, else the
 *   default), so a descent opens with a sensible fraction and never blank.
 * · **AMRAP** → stamps the rep FLOOR the backend requires. The effort cap is OPTIONAL and is never
 *   seeded: an auto-seeded cap reinstates the mandate through the back door, and on a movement
 *   measuring neither RPE nor RIR it would refuse the save outright.
 * · **Warmup / Working / Top** → drops the percent the set was riding **as a descent**, and only
 *   that one. `prescribedPercent` is two prescriptions wearing one field name — a back-off's
 *   fraction-of-top, and every other role's %-of-1RM — and clearing it unconditionally destroyed an
 *   authored %1RM every time a heal demoted a top on a percent-mode exercise.
 * · anything else → just the type.
 *
 * Pure: the caller applies the patch, and `draftOperations` re-normalises the load against the
 * exercise's prescription mode and repairs the block afterwards.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

import type { ExerciseDraft, SetDraft } from './planDraft.types';
import { carriesBand } from './progressionBand';
import {
  AMRAP_SET_TYPE,
  BACKOFF_SET_TYPE,
  TOP_SET_TYPE,
  WORKING_SET_TYPE,
  backoffPick,
  isAmrapSet,
  topSetCandidates,
} from './setShape';

/** The warm-up type, taken from the counting rule's own vocabulary rather than restated. */
export const WARMUP_SET_TYPE = 'Warmup';

/**
 * The five types this app authors, in the order the badge CYCLES through them and the order the
 * menu lists them: R → W → T → B → A → R.
 *
 * Warm-up leads because it is the structural odd one out — the only type outside every counter —
 * and because the canon prints the badges in that order down a table.
 */
export const AUTHORABLE_SET_TYPES: readonly string[] = [
  WARMUP_SET_TYPE,
  WORKING_SET_TYPE,
  TOP_SET_TYPE,
  BACKOFF_SET_TYPE,
  AMRAP_SET_TYPE,
];

/**
 * The next type in the cycle.
 *
 * A type this build does not author — an inbound `Dropset`, or a null — enters the cycle at
 * `Working` rather than being refused. The trainer pressing the badge is asking for a type they can
 * reason about, and `Working` is the neutral one.
 */
export function nextSetTypeInCycle(current: string | null): string {
  const index = current === null ? -1 : AUTHORABLE_SET_TYPES.indexOf(current);
  if (index === -1) return WORKING_SET_TYPE;
  return AUTHORABLE_SET_TYPES[(index + 1) % AUTHORABLE_SET_TYPES.length] ?? WORKING_SET_TYPE;
}

/** The rep floor a set gets when it becomes AMRAP with none authored. The backend requires >= 1. */
export const AMRAP_FLOOR_DEFAULT = 5;

/** The roles that hang off no top: retyping into one drops the fraction a DESCENT was riding. */
const ROLES_WITHOUT_A_TOP: readonly string[] = [WARMUP_SET_TYPE, WORKING_SET_TYPE, TOP_SET_TYPE];

/**
 * What a finished role interaction ASKS FOR — the two acts, stated apart from where they land.
 *
 * The role chooser is shared by two surfaces, the set table and the progression sheet's row badge,
 * and BOTH write straight into the plan draft through `changeSetType`: retyping a set is its own
 * decision with its own undo entry, which is why `progressionStaging` left `roleChange` out. Naming
 * the ACT rather than the dispatch is what lets one control serve both without either surface
 * reimplementing the transitions. An earlier revision of this note said the sheet staged the change
 * and committed it on „Zastosuj"; it never did, and the staging module's own header says so.
 */
export type SetRoleChange =
  | { readonly kind: 'setType'; readonly setLocalId: string; readonly nextType: string }
  | {
      readonly kind: 'addBackoffDescent';
      readonly setLocalId: string;
      /** The row to bind as the block's top, or null when the exercise already has one. */
      readonly topLocalId: string | null;
    };

/**
 * Why a role may NOT be picked for this row, or null when it may. A surface RENDERS the refusal; it
 * does not decide it, which is what keeps two surfaces that author roles from disagreeing.
 *
 * · `topIsGranted` — a TOP is a role the block hands out and takes back, never a label applied by
 *   hand. It is listed only on the row that already holds one, so the chooser can describe what it
 *   is looking at.
 * · `repsNotMeasured` — an AMRAP's rep floor is MANDATORY (the backend refuses a floor-less one) and
 *   R9 refuses a prescription on a metric the exercise does not measure. On a movement measuring
 *   neither, the two rules meet: every AMRAP it could hold is illegal, and offering it produces a
 *   draft that cannot be saved and cannot be walked back.
 *
 * A row ALREADY typed AMRAP keeps its option selectable — refusing the row's own current role would
 * leave the chooser with nothing checked and no way to retype out of the shape that needs escaping.
 */
export type SetTypeRefusal = 'topIsGranted' | 'repsNotMeasured';

export function setTypeRefusal(
  type: string,
  currentType: string | null,
  measurementFields: readonly MeasurementFieldKey[],
): SetTypeRefusal | null {
  if (type === TOP_SET_TYPE) return currentType === TOP_SET_TYPE ? 'topIsGranted' : null;
  if (type === AMRAP_SET_TYPE && currentType !== AMRAP_SET_TYPE) {
    return measurementFields.includes('reps') ? null : 'repsNotMeasured';
  }
  return null;
}

/**
 * The patch that changes `target`'s role to `nextType`. `sets` is the exercise's full list, read
 * only to prefill a back-off's percent from the previous descent.
 *
 * Two prescriptions are dropped by LEAVING a role rather than by arriving at one, and both used to
 * be carried across silently — see the two helpers below.
 */
export function setTypePatch(
  target: SetDraft,
  nextType: string,
  sets: readonly SetDraft[],
): Partial<SetDraft> {
  return withoutAnOrphanedFloor(target, withoutAnOrphanedCeiling(target, rolePatch(target, nextType, sets)));
}

/**
 * A row that has just STOPPED carrying the band drops its ceiling in the same patch — the invariant
 * `progressionBand` states.
 *
 * Warm-ups and AMRAP rows carry no band, for the reasons that module spells out. But a working row
 * retyped into either KEPT the `prescribedCeiling` it had, and a ceiling on a row nothing reads it
 * on is worse than a stale number: `bandOf` still finds it, so an exercise whose every banded row
 * had been retyped away still reported a band, and the ceiling shipped to a backend that reads a bar
 * the client can never be judged against. Written against the RESULTING row rather than the role
 * name, so the two readings of "carries a band" can only ever agree.
 */
function withoutAnOrphanedCeiling(target: SetDraft, patch: Partial<SetDraft>): Partial<SetDraft> {
  const next = { ...target, ...patch };
  if (carriesBand(next) || next.prescribedCeiling == null) return patch;
  return { ...patch, prescribedCeiling: null };
}

/**
 * A row that has just STOPPED being an AMRAP drops the rep FLOOR it carried as one.
 *
 * "5+" and "5" are not the same prescription: the first is a floor the client is asked to beat, the
 * second a count they are asked to hit. Carrying the number across a retype silently converts one
 * into the other. On a movement that does not measure repetitions it does something worse — the
 * AMRAP transition seeds the floor the backend requires, and retyping back to Working left that seed
 * standing on a column the exercise has no contract for, so every subsequent save failed R9 with no
 * cell on screen holding the offending number.
 *
 * Keyed on LEAVING the role rather than on the destination, so it fires for warm-up, working, top
 * and back-off alike, and never touches a floor on a row that is still an AMRAP.
 */
function withoutAnOrphanedFloor(target: SetDraft, patch: Partial<SetDraft>): Partial<SetDraft> {
  const next = { ...target, ...patch };
  if (!isAmrapSet(target) || isAmrapSet(next) || next.prescribedReps == null) return patch;
  return { ...patch, prescribedReps: null };
}

function rolePatch(
  target: SetDraft,
  nextType: string,
  sets: readonly SetDraft[],
): Partial<SetDraft> {
  if (nextType === BACKOFF_SET_TYPE) {
    return { setType: BACKOFF_SET_TYPE, prescribedPercent: backoffPick(sets, target).percent };
  }
  if (nextType === AMRAP_SET_TYPE) {
    // The floor is what the backend requires of an AMRAP set; an existing one is kept, because a
    // conversion is not a statement about the reps the trainer already wrote.
    const needsFloor = target.prescribedReps == null || target.prescribedReps < 1;
    return needsFloor
      ? { setType: AMRAP_SET_TYPE, prescribedReps: AMRAP_FLOOR_DEFAULT }
      : { setType: AMRAP_SET_TYPE };
  }
  if (ROLES_WITHOUT_A_TOP.includes(nextType)) {
    // Only a DESCENT's percent is a fraction-of-top with no meaning off the block. Every other
    // row's is its %1RM prescription, and it survives the retype — `normalizeSetLoad` then decides
    // whether the exercise's prescription MODE still keeps it, which is where that question belongs.
    return target.setType === BACKOFF_SET_TYPE
      ? { setType: nextType, prescribedPercent: null }
      : { setType: nextType };
  }
  return { setType: nextType };
}

/**
 * The sets a trainer may bind as this exercise's TOP when they pick "Back-off".
 *
 * Empty means **don't ask** — see `topSetCandidates`. Returns the sets rather than the indexes,
 * because the caller is a menu that has to name them.
 */
export function bindableTopSets(exercise: ExerciseDraft): SetDraft[] {
  return topSetCandidates(exercise.sets)
    .map((index) => exercise.sets[index])
    .filter((set): set is SetDraft => set !== undefined);
}

/**
 * The at-most-one-cap rule, applied to an in-flight patch: an AMRAP set may carry at most one
 * effort cap, so **committing** a value into one effort field clears the other.
 *
 * The rule fires on a COMMIT and only on a commit. A bare role change — a working set carrying both
 * an RPE and a RIR being turned into an AMRAP — keeps both: the trainer authored them, the
 * conversion is not a statement about either, and quietly deleting one at that moment destroys
 * authored work with nothing on screen to say so. The resulting two-cap set is a shape the backend
 * refuses, so it is MARKED in the table and resolved deterministically at the wire
 * (`amrapShippedCaps`) — a save can never ship both.
 *
 * Clearing a cap never touches the other, and an AMRAP set with no cap at all is a legal bare
 * floor. This is soft exclusivity, not a lock: both cells stay editable.
 */
export function amrapCapExclusivityPatch(
  set: SetDraft,
  patch: Partial<SetDraft>,
): Partial<SetDraft> {
  const next = { ...set, ...patch };
  if (!isAmrapSet(next)) return patch;
  if (next.prescribedRpe == null || next.prescribedRir == null) return patch;

  const kept =
    patch.prescribedRpe != null ? 'prescribedRpe' : patch.prescribedRir != null ? 'prescribedRir' : null;
  if (kept === null) return patch;
  return { ...patch, [kept === 'prescribedRpe' ? 'prescribedRir' : 'prescribedRpe']: null };
}

/** True when an AMRAP set holds BOTH caps — a shape the backend refuses, so the table says so. */
export function hasAmrapCapConflict(set: SetDraft): boolean {
  return isAmrapSet(set) && set.prescribedRpe != null && set.prescribedRir != null;
}

/**
 * Which of two simultaneously-held caps SURVIVES the save, so the warning can name it rather than
 * guess. RIR when the exercise measures both — mobile's historical default, carried over verbatim,
 * because the point is not that RIR is better but that both clients must drop the same one.
 */
export function survivingAmrapCap(
  measurementFields: readonly MeasurementFieldKey[],
): 'rpe' | 'rir' {
  const preference: readonly ('rir' | 'rpe')[] = ['rir', 'rpe'];
  return preference.find((metric) => measurementFields.includes(metric)) ?? 'rir';
}
